import type { Dispatch, SetStateAction } from "react";
import type { GhlNewConnectionDraft } from "./ghlSectionShared";

type GhlAddConnectionFormProps = {
  newConn: GhlNewConnectionDraft;
  setNewConn: Dispatch<SetStateAction<GhlNewConnectionDraft>>;
  loading: boolean;
  onSave: () => void;
  onCancel: () => void;
};

export function GhlAddConnectionForm({
  newConn,
  setNewConn,
  loading,
  onSave,
  onCancel,
}: GhlAddConnectionFormProps) {
  return (
    <div className="border border-dashed border-gray-300 rounded-lg p-4 space-y-2 bg-gray-50">
      <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">New connection</div>
      <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
        <strong>Adding multiple GHL instances?</strong> This form is for connecting an additional GHL sub-account to
        the same org. To rotate the API key on an existing connection, close this and use the{" "}
        <strong>Update key</strong> button on the connection card above.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input
          className="text-sm border border-gray-200 rounded-lg px-3 py-2"
          placeholder="Company name (e.g. Acme Shed Co.)"
          value={newConn.company_name}
          onChange={(e) => setNewConn((c) => ({ ...c, company_name: e.target.value }))}
        />
        <div className="space-y-0.5">
          <input
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 font-mono"
            placeholder="GHL Sub-Account ID"
            value={newConn.ghl_location_id}
            onChange={(e) => setNewConn((c) => ({ ...c, ghl_location_id: e.target.value }))}
          />
          <p className="text-[10px] text-gray-400 px-1">Found in GHL: Settings → Business Info, or in the URL after /location/</p>
        </div>
      </div>
      <input
        type="password"
        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 font-mono"
        placeholder="GHL private integration API key"
        value={newConn.ghl_api_key}
        onChange={(e) => setNewConn((c) => ({ ...c, ghl_api_key: e.target.value }))}
      />
      <p className="text-xs text-gray-400">
        GHL: Settings › Private Integrations — scopes: Opportunities (Read), Contacts (Read), Locations, Social Planner Posts + Accounts (Read)
        (locations.readonly).
      </p>
      <div className="space-y-0.5">
        <input
          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 font-mono"
          placeholder="Webhook URL (GHL trigger URL for lead delivery)"
          value={newConn.webhook_url}
          onChange={(e) => setNewConn((c) => ({ ...c, webhook_url: e.target.value }))}
        />
        <p className="text-[10px] text-gray-400 px-1">
          GHL: Automation → Webhooks — paste the trigger URL here. Leave blank to use the env-var fallback.
        </p>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => void onSave()}
          disabled={loading}
          className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50"
        >
          Save connection
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
